// Centralized status color configuration
// Based on the reference image with adjusted colors for distinction

export const STATUS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  // To-do section
  'backlog': {
    bg: 'bg-gray-200',
    text: 'text-gray-800',
    border: 'border-gray-400'
  },
  'planned': {
    bg: 'bg-gray-100',
    text: 'text-gray-700',
    border: 'border-gray-300'
  },
  
  // In progress section
  'in progress': {
    bg: 'bg-blue-100',
    text: 'text-blue-800',
    border: 'border-blue-300'
  },
  'in-progress': {
    bg: 'bg-blue-100',
    text: 'text-blue-800',
    border: 'border-blue-300'
  },
  'quality check': {
    bg: 'bg-orange-100',
    text: 'text-orange-800',
    border: 'border-orange-300'
  },
  'quality-check': {
    bg: 'bg-orange-100',
    text: 'text-orange-800',
    border: 'border-orange-300'
  },
  'maintenance': {
    bg: 'bg-purple-100',
    text: 'text-purple-800',
    border: 'border-purple-300'
  },
  
  // Complete section
  'done': {
    bg: 'bg-green-100',
    text: 'text-green-800',
    border: 'border-green-300'
  },
  'completed': {
    bg: 'bg-green-100',
    text: 'text-green-800',
    border: 'border-green-300'
  },
  'archived': {
    bg: 'bg-slate-200',
    text: 'text-slate-700',
    border: 'border-slate-400'
  },
  
  // Fallback for unknown statuses
  'default': {
    bg: 'bg-neutral-100',
    text: 'text-neutral-800',
    border: 'border-neutral-300'
  }
};

// Helper function to get status color classes
export function getStatusColorClasses(status: string): string {
  const normalizedStatus = status?.toLowerCase().trim() || 'default';
  const colors = STATUS_COLORS[normalizedStatus] || STATUS_COLORS['default'];
  return `${colors.bg} ${colors.text} ${colors.border}`;
}

// Helper function to get individual color properties
export function getStatusColors(status: string): { bg: string; text: string; border: string; borderColorValue: string } {
  const normalizedStatus = status?.toLowerCase().trim() || 'default';
  const colors = STATUS_COLORS[normalizedStatus] || STATUS_COLORS['default'];
  
  // Map border classes to actual color values for inline styles
  const borderColorMap: Record<string, string> = {
    'border-gray-400': '#9ca3af',
    'border-gray-300': '#d1d5db',
    'border-blue-300': '#93c5fd',
    'border-orange-300': '#fdba74',
    'border-purple-300': '#d8b4fe',
    'border-green-300': '#86efac',
    'border-slate-400': '#cbd5e1',
    'border-neutral-300': '#d4d4d8'
  };
  
  return {
    ...colors,
    borderColorValue: borderColorMap[colors.border] || '#d4d4d8'
  };
}

// Get color for Badge component variant (for backward compatibility)
export function getStatusBadgeVariant(status?: string): "outline" | "default" | "secondary" | "destructive" {
  const normalizedStatus = status?.toLowerCase().trim() || '';
  
  if (normalizedStatus.includes('done') || normalizedStatus.includes('completed')) {
    return 'default';
  }
  if (normalizedStatus.includes('progress') || normalizedStatus.includes('quality')) {
    return 'secondary';
  }
  if (normalizedStatus.includes('backlog') || normalizedStatus.includes('planned')) {
    return 'outline';
  }
  
  return 'outline';
}

// Helper to get border-left color class for project cards
export function getStatusBorderLeftClass(status: string): string {
  const normalizedStatus = status?.toLowerCase().trim() || 'default';
  
  const borderMap: Record<string, string> = {
    'backlog': 'border-l-gray-400',
    'planned': 'border-l-gray-300',
    'in progress': 'border-l-blue-300',
    'in-progress': 'border-l-blue-300',
    'quality check': 'border-l-orange-300',
    'quality-check': 'border-l-orange-300',
    'maintenance': 'border-l-purple-300',
    'done': 'border-l-green-300',
    'completed': 'border-l-green-300',
    'archived': 'border-l-slate-400',
    'default': 'border-l-neutral-300'
  };
  
  return borderMap[normalizedStatus] || borderMap['default'];
}
